import {
    Box,
    Divider,
    FormControlLabel,
    Radio,
    RadioGroup,
    Slider,
    sliderClasses,
    styled,
    Tooltip,
    Typography,
} from "@mui/material";
import { Switch } from "@procore/core-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { SlottedWindow } from "components/window/SlottedWindow";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { type BaseWindowProps } from "contexts/windows";
import {
    renderActions,
    selectDefaultPointVisualization,
    selectPoints,
    selectSubtrees,
    SubtreeStatus,
} from "features/render";

const SectionTitle = styled(Typography)`
    font-weight: 700;
    font-size: 15px;
    line-height: 20px;
    margin-bottom: 12px;
`;

const FieldLabel = styled(Typography)`
    font-size: 13px;
    color: ${({ theme }) => theme.palette.text.primary};
    margin-bottom: 4px;
`;

const SectionDivider = styled(Divider)`
    margin: 8px -20px 0;
`;

const ScannerLocationsRow = styled(Box)`
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 16px;
    opacity: ${({ theme }) => theme.palette.action.disabledOpacity};
`;

/**
 * Slider styled to match the Figma point-cloud settings (mock-style track with
 * evenly spaced tick marks, blue active portion, white thumb with thin border).
 */
const StyledSlider = styled(Slider)`
    height: 6px;
    padding: 14px 0;

    & .${sliderClasses.rail} {
        opacity: 1;
        background-color: ${({ theme }) => theme.palette.grey[300]};
        height: 6px;
        border-radius: 3px;
    }

    & .${sliderClasses.track} {
        height: 6px;
        border: none;
        border-radius: 3px;
    }

    & .${sliderClasses.thumb} {
        height: 20px;
        width: 20px;
        background-color: #ffffff;
        border: 1px solid ${({ theme }) => theme.palette.grey[400]};
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);

        &:hover,
        &.${sliderClasses.focusVisible} {
            box-shadow: 0 0 0 6px rgba(25, 118, 210, 0.12);
        }
    }

    & .${sliderClasses.mark} {
        background-color: ${({ theme }) => theme.palette.grey[400]};
        height: 6px;
        width: 1px;
        margin-top: 0;

        &.${sliderClasses.markActive} {
            background-color: ${({ theme }) => theme.palette.primary.light};
            opacity: 0.6;
        }
    }

    & .${sliderClasses.markLabel} {
        font-size: 11px;
        color: ${({ theme }) => theme.palette.text.secondary};
        top: 24px;
    }
`;

const SliderField = styled(Box)`
    padding-bottom: 24px; /* leave space for the mark labels below the track */
`;

type PointVisualizationKind = "color" | "intensity" | "classification" | "elevation";

/**
 * Marks helper – evenly spaced ticks. Labels only at the endpoints + the middle
 * so the row stays readable on narrow phones; this matches the visual rhythm
 * of the Figma without the labels colliding.
 */
function makeMarks(min: number, max: number, steps: number, formatter: (v: number) => string) {
    const result: { value: number; label?: string }[] = [];
    for (let i = 0; i <= steps; i++) {
        const value = min + ((max - min) * i) / steps;
        const isLabeled = i === 0 || i === steps || i === Math.floor(steps / 2);
        result.push({ value, label: isLabeled ? formatter(value) : undefined });
    }
    return result;
}

const BASE_POINT_SIZE_MARKS = makeMarks(0, 2, 4, (v) => v.toFixed(1));
const MAX_POINT_SIZE_MARKS = makeMarks(1, 100, 5, (v) => `${Math.round(v)}`);
const POINT_DENSITY_MARKS = makeMarks(0, 2, 4, (v) => v.toFixed(1));

/**
 * Point Cloud Settings window.
 *
 * Mirrors the Figma design: a "Display" section with three full-width sliders
 * and a (disabled placeholder) "Show Scanner Locations" toggle, followed by a
 * "Color By" radio group. Bindings:
 *  - Base point size      → `points.size.pixel`     (renderActions.setPoints)
 *  - Max point size       → `points.size.maxPixel`
 *  - Point density        → `points.size.toleranceFactor`
 *  - Color By             → `points.defaultPointVisualization.kind`
 *     - True Color        → "color"
 *     - Grayscale         → "intensity"     (the @novorender/api intensity
 *                                            visualization is a grayscale ramp)
 *     - Classification    → "classification"
 *     - Elevation         → "elevation"
 *
 * "Show Scanner Locations" is rendered to match the Figma layout but is
 * currently a disabled placeholder: there is no backing slice field or render
 * pipeline support for scanner positions yet. Wire it up once that lands.
 */
export function PointCloudSettingsWindow({ onClose, position }: BaseWindowProps) {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const points = useAppSelector(selectPoints);
    const subtrees = useAppSelector(selectSubtrees);
    const defaultPointVisualization = useAppSelector(selectDefaultPointVisualization);
    const {
        state: { view },
    } = useExplorerGlobals();

    const [basePointSize, setBasePointSize] = useState(points.size.pixel);
    const [maxPointSize, setMaxPointSize] = useState(points.size.maxPixel);
    const [pointDensity, setPointDensity] = useState(points.size.toleranceFactor);

    // Keep local slider state in sync with external Redux changes (e.g. preset
    // resets) so the thumb position stays accurate when the user re-opens the
    // panel after the underlying setting has been updated elsewhere.
    useEffect(() => {
        setBasePointSize(points.size.pixel);
    }, [points.size.pixel]);
    useEffect(() => {
        setMaxPointSize(points.size.maxPixel);
    }, [points.size.maxPixel]);
    useEffect(() => {
        setPointDensity(points.size.toleranceFactor);
    }, [points.size.toleranceFactor]);

    const isPointsUnavailable = subtrees.points === SubtreeStatus.Unavailable;
    const hasClassification = view?.renderState.scene?.config.hasPointClassification ?? false;
    const hasIntensity = view?.renderState.scene?.config.hasPointIntensity ?? false;

    const colorByValue: PointVisualizationKind = (() => {
        const kind = defaultPointVisualization?.kind;
        if (kind === "classification" || kind === "elevation" || kind === "intensity") {
            return kind;
        }
        return "color";
    })();

    const handleColorByChange = (kind: PointVisualizationKind) => {
        dispatch(renderActions.setPoints({ defaultPointVisualization: { kind } }));
    };

    return (
        <SlottedWindow
            position={position}
            size={{ width: 400 }}
            autoHeight
            onClose={onClose}
            headerRegion={<SlottedWindow.Title>{t("pointCloudSettings")}</SlottedWindow.Title>}
            data-testid="point-cloud-settings-window"
        >
            <Box sx={{ padding: "16px 20px 24px", display: "flex", flexDirection: "column", gap: "16px" }}>
                <Box>
                    <SectionTitle variant="subtitle1">{t("display")}</SectionTitle>

                    <SliderField>
                        <FieldLabel>{t("basePointSize")}</FieldLabel>
                        <StyledSlider
                            aria-label={t("basePointSize")}
                            min={0}
                            max={2}
                            step={0.1}
                            marks={BASE_POINT_SIZE_MARKS}
                            value={basePointSize}
                            valueLabelDisplay="auto"
                            disabled={isPointsUnavailable}
                            onChange={(_evt, value) => {
                                if (!Array.isArray(value)) {
                                    setBasePointSize(value);
                                }
                            }}
                            onChangeCommitted={(_evt, value) => {
                                if (!Array.isArray(value)) {
                                    dispatch(renderActions.setPoints({ size: { pixel: value } }));
                                }
                            }}
                        />
                    </SliderField>

                    <SliderField>
                        <FieldLabel>{t("maxPointSize")}</FieldLabel>
                        <StyledSlider
                            aria-label={t("maxPointSize")}
                            min={1}
                            max={100}
                            step={1}
                            marks={MAX_POINT_SIZE_MARKS}
                            value={maxPointSize}
                            valueLabelDisplay="auto"
                            disabled={isPointsUnavailable}
                            onChange={(_evt, value) => {
                                if (!Array.isArray(value)) {
                                    setMaxPointSize(value);
                                }
                            }}
                            onChangeCommitted={(_evt, value) => {
                                if (!Array.isArray(value)) {
                                    dispatch(renderActions.setPoints({ size: { maxPixel: value } }));
                                }
                            }}
                        />
                    </SliderField>

                    <SliderField>
                        <FieldLabel>{t("pointDensity")}</FieldLabel>
                        <StyledSlider
                            aria-label={t("pointDensity")}
                            min={0}
                            max={2}
                            step={0.05}
                            marks={POINT_DENSITY_MARKS}
                            value={pointDensity}
                            valueLabelDisplay="auto"
                            disabled={isPointsUnavailable}
                            onChange={(_evt, value) => {
                                if (!Array.isArray(value)) {
                                    setPointDensity(value);
                                }
                            }}
                            onChangeCommitted={(_evt, value) => {
                                if (!Array.isArray(value)) {
                                    dispatch(renderActions.setPoints({ size: { toleranceFactor: value } }));
                                }
                            }}
                        />
                    </SliderField>

                    <Tooltip title={t("scannerLocationsNotSupported")} placement="top">
                        <ScannerLocationsRow>
                            <Switch checked={false} onChange={() => undefined} id="show-scanner-locations" />
                            <Typography component="label" htmlFor="show-scanner-locations" sx={{ fontSize: 14 }}>
                                {t("showScannerLocations")}
                            </Typography>
                        </ScannerLocationsRow>
                    </Tooltip>
                </Box>

                <SectionDivider />

                <Box>
                    <SectionTitle variant="subtitle1">{t("colorBy")}</SectionTitle>
                    <RadioGroup
                        value={colorByValue}
                        onChange={(_evt, value) => handleColorByChange(value as PointVisualizationKind)}
                        sx={{ gap: "4px" }}
                    >
                        <FormControlLabel value="color" control={<Radio />} label={t("trueColor")} />
                        <FormControlLabel
                            value="intensity"
                            control={<Radio />}
                            label={t("grayscale")}
                            disabled={!hasIntensity}
                        />
                        <FormControlLabel
                            value="classification"
                            control={<Radio />}
                            label={t("classification")}
                            disabled={!hasClassification}
                        />
                        <FormControlLabel value="elevation" control={<Radio />} label={t("elevation")} />
                    </RadioGroup>
                </Box>
            </Box>
        </SlottedWindow>
    );
}
